import Confetti from 'react-confetti'
import styled from 'styled-components'
import { device } from '@/utils'
import { Button, PoweredByRever } from '@itsrever/design-system'
import { useTranslate, usePages } from '@/hooks'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useNavigate, useSearchParams } from 'react-router'
import { useAppSelector } from '@/redux/hooks'
import { PbECommerceSettingsRefundExchangeFlowEnum } from '@itsrever/rever-api'
import { useEffect } from 'react'
import posthog from 'posthog-js'
import { usePostHog } from '@/application/use-posthog'
import { ExchangeFlows } from '@/utils/types'
import KYIInfoBanner from '@/components/KYIInfoBanner/KYIInfoBanner'
import HoffTopBar from '@/components/HoffTopBar/hoff-top-bar'
import TopBar from '@/components/TopBarComponent/TopBar'

function SuccessTrackingPage() {
    const { captureEventPosthog } = usePostHog()
    const { translate } = useTranslate()

    const navigate = useNavigate()
    const { pages } = usePages()

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )
    const webUrl = settings.website_url
    const [params] = useSearchParams()

    let processId =
        useAppSelector((store) => store.order.selectedProcessID) ?? ''
    processId ||= params.get('processId') ?? ''
    let orderNumber = useAppSelector((store) => store.order.orderNumber)
    orderNumber ||= params.get('orderNumber') ?? ''
    let email = useAppSelector((store) => store.order.email)
    email ||= params.get('email') ?? ''

    const confettiOff = useFeatureFlagEnabled('confettiOff')

    const isKeepYourItem = useAppSelector((s) => s.return.keepYourItem)

    const amount = params.get('amount')
    const currency = params.get('currency')
    const returnedItemsCount = params.get('returnedItemsCount')
    const exchangedOneOnOneItemsCount = params.get(
        'exchangedOneOnOneItemsCount'
    )
    const exchangedFullCatalogItemsCount = parseInt(
        params.get('exchangedFullCatalogItemsCount') || '0'
    )

    const exchangeFlow = toExchangeOptionExchangeFlow(settings.exchange_flow)

    const refundMethod = params.get('refundMethod')

    useEffect(() => {
        if (email) {
            posthog?.identify(email)
        }
        if (!currency || !amount) return
        captureEventPosthog('Completed', {
            Amount: amount,
            Currency: currency,
            returnedItemsCount,
            refundMethod,
            exchangedOneOnOneItemsCount,
            exchangedFullCatalogItemsCount,
            exchangeFlow,
            ecommerceId: settings.ecommerce_id
        })

        if (exchangedFullCatalogItemsCount > 0) {
            captureEventPosthog('Completed with FC exchange', {
                ecommerceId: settings.ecommerce_id
            })
        }
        params.delete(currency, amount)
    }, [])

    const isMisako = settings.ecommerce_group === 'misako'
    const isHoff = settings.ecommerce_group === 'hoff'

    const logoUrl = settings.branding?.logo_url
    const title = settings.branding?.title
    const mobileLogoHeight = settings.branding?.logo_height_mobile

    if (isHoff) {
        return (
            <>
                <TopBar page="order" />
                <div className="relative mx-auto flex size-full h-full max-w-screen-xl grow flex-col items-center bg-cover bg-center bg-no-repeat px-10">
                    <HoffTopBar />
                    <p
                        className="mt-10 font-haas text-xl font-light"
                        dangerouslySetInnerHTML={{
                            __html: translate('success_title')
                        }}
                    />

                    <p
                        className="mb-8 font-haas text-lg font-light"
                        dangerouslySetInnerHTML={{
                            __html: isKeepYourItem
                                ? ''
                                : translate(
                                      `tracking_success_return_processed`
                                  ) + '.'
                        }}
                    />

                    <p
                        className="max-w-[1000px] text-center font-haas text-sm font-light text-neutral-500"
                        dangerouslySetInnerHTML={{
                            __html: isKeepYourItem
                                ? ''
                                : translate(`tracking_success_receive_email`)
                        }}
                    />
                    {webUrl ? (
                        <Button
                            className="mt-10"
                            variant="text"
                            type="submit"
                            onClick={() => (window.location.href = webUrl)}
                        >
                            <p
                                className="underline underline-offset-4"
                                dangerouslySetInnerHTML={{
                                    __html: translate(
                                        'success_continue_shopping'
                                    )
                                }}
                            />
                        </Button>
                    ) : null}
                    <div className="mb-5 mt-auto">
                        <PoweredByRever />
                    </div>
                </div>
            </>
        )
    }

    if (isMisako) {
        return (
            <div className="flex h-full flex-1 flex-col justify-center  gap-4 p-4 md:px-10 md:py-6">
                <div className="mb-auto flex w-full items-end">
                    {logoUrl ? (
                        <div className="mr-auto">
                            <img
                                style={
                                    {
                                        '--logo-desktop-height': '1.5rem',
                                        '--logo-mobile-height':
                                            mobileLogoHeight ?? '1.5rem'
                                    } as React.CSSProperties
                                }
                                width={285}
                                height={70}
                                src={logoUrl}
                                alt={title}
                                className="h-[var(--logo-mobile-height)] w-auto object-contain md:h-[var(--logo-desktop-height)]"
                            />
                        </div>
                    ) : null}
                    <div className="flex items-center gap-5">
                        <a
                            href="https://www.misako.com/pages/wishlist"
                            target="_blank"
                            rel="noreferrer"
                        >
                            <img
                                src="https://www.misako.com/cdn/shop/files/favorite.svg?v=1731587455"
                                width={20}
                                height={20}
                            />
                        </a>
                        <a
                            href="https://www.misako.com/account"
                            target="_blank"
                            rel="noreferrer"
                        >
                            <svg
                                width="20"
                                height="21"
                                viewBox="0 0 20 21"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M19 20V17.7778C19 15.3222 16.9863 13.3333 14.5 13.3333H5.5C3.01375 13.3333 1 15.3222 1 17.7778V20"
                                    stroke="rgb(21, 21, 21)"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                ></path>
                                <path
                                    d="M10 9C12.2091 9 14 7.20914 14 5C14 2.79086 12.2091 1 10 1C7.79086 1 6 2.79086 6 5C6 7.20914 7.79086 9 10 9Z"
                                    stroke="rgb(21, 21, 21)"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                ></path>
                            </svg>
                        </a>
                        <a
                            href="https://www.misako.com/collections/top-ventas"
                            target="_blank"
                            rel="noreferrer"
                        >
                            <svg
                                width="19"
                                height="19"
                                viewBox="0 0 19 19"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M13.6969 13.9469L18 18.25M15.875 8.6875C15.875 12.7951 12.5451 16.125 8.4375 16.125C4.32988 16.125 1 12.7951 1 8.6875C1 4.57988 4.32988 1.25 8.4375 1.25C12.5451 1.25 15.875 4.57988 15.875 8.6875Z"
                                    stroke="rgb(21, 21, 21)"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                ></path>
                            </svg>
                        </a>
                    </div>
                </div>

                <div className="mt-24 flex-1">
                    <h3
                        className="font-[400] tracking-wider [font-family:Inter]"
                        dangerouslySetInnerHTML={{
                            __html: translate('success_title')
                        }}
                    />

                    <h6
                        className="mb-8 mt-2 font-[300] tracking-wider [font-family:Inter]"
                        dangerouslySetInnerHTML={{
                            __html: isKeepYourItem
                                ? ''
                                : translate(
                                      `tracking_success_return_processed`
                                  ) + '.'
                        }}
                    />
                    <p
                        className=" text-[15px] font-[300] tracking-wider [font-family:Inter]"
                        dangerouslySetInnerHTML={{
                            __html: isKeepYourItem
                                ? ''
                                : translate(`tracking_success_receive_email`)
                        }}
                    />
                    {webUrl ? (
                        <Button
                            className="mt-10"
                            variant="text"
                            type="submit"
                            onClick={() => (window.location.href = webUrl)}
                        >
                            <p
                                className="[font-family] text-[13px] uppercase tracking-wider underline underline-offset-8"
                                dangerouslySetInnerHTML={{
                                    __html: translate(
                                        'success_continue_shopping'
                                    )
                                }}
                            />
                        </Button>
                    ) : null}
                </div>
                <div className="mb-5 mt-auto flex w-full">
                    <PoweredByRever />
                </div>
            </div>
        )
    }

    return (
        <MainDiv>
            <Card>
                {confettiOff ? null : (
                    <Confetti
                        initialVelocityY={20}
                        recycle={false}
                        id="confetti"
                    />
                )}
                <h3
                    dangerouslySetInnerHTML={{
                        __html: translate('success_title')
                    }}
                />

                <h6
                    className="mb-8 mt-2"
                    dangerouslySetInnerHTML={{
                        __html: isKeepYourItem
                            ? ''
                            : translate(`tracking_success_return_processed`) +
                              '.'
                    }}
                />

                {isKeepYourItem ? (
                    <p className="flex flex-col gap-4 pb-8">
                        <KYIInfoBanner />
                        <span
                            dangerouslySetInnerHTML={{
                                __html: translate(`kyi_success_click_button`)
                            }}
                        />
                    </p>
                ) : (
                    <p
                        className="pb-8"
                        dangerouslySetInnerHTML={{
                            __html: translate(`tracking_success_click_button`)
                        }}
                    />
                )}
                <Button
                    onClick={() =>
                        navigate(
                            `${pages.Tracking}?processId=${encodeURIComponent(
                                processId
                            )}&orderNumber=${encodeURIComponent(
                                orderNumber || ''
                            )}&email=${encodeURIComponent(
                                email || ''
                            )}&source=success_page`,
                            {
                                replace: true
                            }
                        )
                    }
                >
                    {isKeepYourItem
                        ? translate('tracking_success_kyi_action_button')
                        : translate('tracking_success_see_return_instructions')}
                </Button>
                <p
                    className="pt-8"
                    dangerouslySetInnerHTML={{
                        __html: isKeepYourItem
                            ? ''
                            : translate(`tracking_success_receive_email`)
                    }}
                />
                {webUrl ? (
                    <Button
                        className="mt-10"
                        variant="text"
                        type="submit"
                        onClick={() => (window.location.href = webUrl)}
                    >
                        <p
                            className="underline underline-offset-4"
                            dangerouslySetInnerHTML={{
                                __html: translate('success_continue_shopping')
                            }}
                        />
                    </Button>
                ) : null}
            </Card>
        </MainDiv>
    )
}

const Card = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: fit-content;
    min-height: 50%;
    background: #ffffff;
    margin-top: 1.5rem;
    padding: 1rem;
    @media ${device.lg} {
        margin-bottom: 1rem;
        padding: 1.5rem 2rem 1.5rem 2rem;
        box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
        border-radius: 10px;
        margin-top: 2.5rem;
    }
`

const MainDiv = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    align-items: center;
    justify-content: center;
    text-align: center;
    @media ${device.lg} {
        background-color: #f2f2f2;
        padding: 2rem;
    }
`

export default SuccessTrackingPage

function toExchangeOptionExchangeFlow(
    exchangeFlow: PbECommerceSettingsRefundExchangeFlowEnum | undefined
): (typeof ExchangeFlows)[keyof typeof ExchangeFlows] {
    switch (exchangeFlow) {
        case PbECommerceSettingsRefundExchangeFlowEnum.Open:
            return ExchangeFlows.Open
        case PbECommerceSettingsRefundExchangeFlowEnum.OpenWithGiftCard:
            return ExchangeFlows.OpenWithGiftCard
        case PbECommerceSettingsRefundExchangeFlowEnum.OneOnOne:
            return ExchangeFlows.OneOnOne
        case PbECommerceSettingsRefundExchangeFlowEnum.Full:
            return ExchangeFlows.Full
        default:
            return ExchangeFlows.None
    }
}
